// src/features/settings/pages/SettingsPage.jsx
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Tabs, Tab, Typography } from '@mui/material';
import { Settings as SettingsIcon, EventBusy, Inventory, PlaceOutlined, AccessTime, Gavel, QrCodeScanner, CoPresent } from '@mui/icons-material';
import NonWorkingDaysPanel from '../components/non-working-days-panel';
import ProductGroupsPanel from '../components/product-groups-panel';
import StockLocationsPanel from '../components/stock-locations-panel';
import RegistrationCodesPanel from '../components/registration-codes-panel';
import OverenskomsterPanel from '../components/overenskomster-panel';
import ScannersPanel from '../components/scanners-panel';
import InfoscreensPanel from '../../infoscreens/components/InfoscreensPanel';
import {
  useProductGroupPermissions,
  useStockLocationPermissions,
  useTidsregistreringRegistrationCodePermissions,
  useTidsregistreringOverenskomstPermissions,
  useNonWorkingDayPermissions,
  useScannerPermissions,
  useInfoscreenPermissions,
} from '../../auth/hooks/usePermissions';

import PageHeader from '../../../components/common/PageHeader';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { t } = useTranslation(['settings', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const nonWorkingDayPermissions = useNonWorkingDayPermissions();
  const productGroupPermissions = useProductGroupPermissions();
  const stockLocationPermissions = useStockLocationPermissions();
  const registrationCodePermissions = useTidsregistreringRegistrationCodePermissions();
  const overenskomstPermissions = useTidsregistreringOverenskomstPermissions();
  const scannerPermissions = useScannerPermissions();
  const infoscreenPermissions = useInfoscreenPermissions();

  // Build available tabs based on permissions
  const availableTabs = [];
  if (nonWorkingDayPermissions.canRead) {
    availableTabs.push({
      key: 'non_working_days',
      label: t('settings:tabs.non_working_days'),
      icon: <EventBusy />,
      component: <NonWorkingDaysPanel />,
    });
  }
  if (productGroupPermissions.canRead) {
    availableTabs.push({
      key: 'product_groups',
      label: t('settings:tabs.product_groups'),
      icon: <Inventory />,
      component: <ProductGroupsPanel />,
    });
  }
  if (stockLocationPermissions.canRead && stockLocationPermissions.canUpdate) {
    availableTabs.push({
      key: 'stock_locations',
      label: t('settings:tabs.stock_locations'),
      icon: <PlaceOutlined />,
      component: <StockLocationsPanel />,
    });
  }
  if (scannerPermissions.canRead) {
    availableTabs.push({
      key: 'scanners',
      label: t('settings:tabs.scanners'),
      icon: <QrCodeScanner />,
      component: <ScannersPanel />,
    });
  }
  if (infoscreenPermissions.canRead) {
    availableTabs.push({
      key: 'infoscreens',
      label: t('settings:tabs.infoscreens'),
      icon: <CoPresent />,
      component: <InfoscreensPanel />,
    });
  }
  if (registrationCodePermissions.canRead) {
    availableTabs.push({
      key: 'registration_codes',
      label: t('settings:tabs.registration_codes'),
      icon: <AccessTime />,
      component: <RegistrationCodesPanel />,
    });
  }
  if (overenskomstPermissions.canRead) {
    availableTabs.push({
      key: 'overenskomster',
      label: t('settings:tabs.overenskomster'),
      icon: <Gavel />,
      component: <OverenskomsterPanel />,
    });
  }

  // The tab lives in the URL (?tab=scanners) so other pages can link to it
  const activeTab = Math.max(0, availableTabs.findIndex(tab => tab.key === searchParams.get('tab')));
  const setActiveTab = (index) => setSearchParams({ tab: availableTabs[index].key }, { replace: true });

  // If no tabs available, show message
  if (availableTabs.length === 0) {
    return (
      <Box>
        <PageHeader icon={SettingsIcon} title={t('settings:title')} />
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography color="text.secondary">
            {t('settings:no_permissions')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader icon={SettingsIcon} title={t('settings:title')} subtitle={t('settings:subtitle')} />

      {/* Tabs Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label="settings tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {availableTabs.map((tab, index) => (
            <Tab
              key={tab.key}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              id={`settings-tab-${index}`}
              aria-controls={`settings-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      {availableTabs.map((tab, index) => (
        <TabPanel key={tab.key} value={activeTab} index={index}>
          {tab.component}
        </TabPanel>
      ))}
    </Box>
  );
}
